import React from 'react';

import { Add, ArrowForwardIos, DensityLarge } from '@mui/icons-material';
import { Box, Stack, Typography } from '@mui/material';

import DebugModal from '~/components/DebugModal/DebugModal';

import { exportItemInfo } from '~/services/PromoService';

function formatCurrencyAmount(amount: number | null | undefined): string {
  if (amount == null || Number.isNaN(Number(amount))) {
    return '-';
  }

  return Number(amount).toFixed(2);
}

function getPromoDiscountAmount(discount: unknown): number | null | undefined {
  if (discount == null || typeof discount !== 'object') {
    return undefined;
  }

  const withCamelCase = discount as { discountAmount?: number | null };
  if (withCamelCase.discountAmount != null) {
    return withCamelCase.discountAmount;
  }

  const withSnakeCase = discount as { discount_amount?: number | null };
  return withSnakeCase.discount_amount;
}

function renderDiscountSummary({
  itemDiscountAmount,
  orderDiscountAmount,
  showOrderDiscountAmount,
  metadataLabel,
  metadataValue,
}: {
  itemDiscountAmount?: number | null;
  orderDiscountAmount?: number | null;
  showOrderDiscountAmount?: boolean;
  metadataLabel?: string;
  metadataValue?: string | number | null;
}) {
  const shouldRenderOrderDiscountAmount =
    showOrderDiscountAmount ?? (itemDiscountAmount != null && orderDiscountAmount != null);
  const hasOrderLevelDifference =
    shouldRenderOrderDiscountAmount &&
    itemDiscountAmount != null &&
    orderDiscountAmount != null &&
    Number(itemDiscountAmount) !== Number(orderDiscountAmount);

  return (
    <Box>
      <Typography variant="body2">
        Item discount amount: <strong>{formatCurrencyAmount(itemDiscountAmount)}</strong>
      </Typography>
      {shouldRenderOrderDiscountAmount && (
        <Typography variant="body2">
          Order discount amount: <strong>{formatCurrencyAmount(orderDiscountAmount)}</strong>
        </Typography>
      )}
      {metadataLabel && (
        <Typography variant="body2">
          {metadataLabel}: <strong>{metadataValue ?? '-'}</strong>
        </Typography>
      )}
    </Box>
  );
}

function Internals({ internals }) {
  const promo = internals?.calculator?.asyncFields?.promo;
  const promoDiscounts = promo?.promo_discounts ?? [];

  return (
    <>
      <Stack>
        <DebugModal title={`Order - ${internals.svcOrderOrder.total}`} type="generic" data={internals.svcOrderOrder} />
        <DebugModal
          title={`(${internals.calculator.asyncFields.user.givenName})`}
          type="generic"
          data={internals.calculator.asyncFields.user}
        />
      </Stack>
      <ArrowForwardIos />
      <Stack>
        <DebugModal
          title={`Promo Order - ${internals.promoOrder.items.length} items`}
          type="generic"
          data={internals.promoOrder}
        />
        {internals.promoOrder.items.map((item) => {
          const itemInfo = exportItemInfo(item.itemInfoString);
          const matchingPromoDiscount = promoDiscounts.find((discount) => {
            const discountItemId =
              (discount as { itemId?: string | null }).itemId ?? (discount as { item_id?: string | null }).item_id;
            return discountItemId != null && discountItemId === item.itemId;
          });
          const matchingPromoDiscountAmount = getPromoDiscountAmount(matchingPromoDiscount);

          return (
            <Stack key={item.itemId} spacing={0.5}>
              {renderDiscountSummary({
                itemDiscountAmount: itemInfo?.promo_code_discount_amount,
                orderDiscountAmount: matchingPromoDiscountAmount,
                showOrderDiscountAmount: Boolean(matchingPromoDiscount),
                metadataLabel: 'Promo code discount amount',
                metadataValue: itemInfo?.promo_code_discount_amount,
              })}
              <DebugModal
                title={`${item.categoryBK} - ${
                  itemInfo?.promo_code_discount_amount ? `${itemInfo?.promo_code_discount_amount}/` : ''
                }${item.discountableTotal}`}
                type="generic"
                data={item}
              />
            </Stack>
          );
        })}
      </Stack>
      <Add />
      <Stack>
        {renderDiscountSummary({
          itemDiscountAmount: promo?.discount_total,
          orderDiscountAmount: promo?.voucherTotal,
          metadataLabel: 'Promo discount total',
          metadataValue: promo?.discount_total,
        })}
        <DebugModal title="Promo" type="generic" data={internals.calculator.asyncFields.promo} />
        <DebugModal title="Discounts" type="generic" data={internals.calculator.asyncFields.promo?.promo_discounts} />
      </Stack>
      <DensityLarge />
    </>
  );
}

export default Internals;
